
import React, { useState, useEffect } from 'react';
import { ref, onValue, set, update, get } from '@firebase/database';
import { db, auth } from '../../firebase';
import { createUserWithEmailAndPassword, getAuth, signOut } from '@firebase/auth';
import { initializeApp, deleteApp } from '@firebase/app';
import { UserProfile, Role } from '../../types';
import { UserCog, Check, X, Loader2, UserPlus, Edit2, Trash2, ShieldCheck, Mail, User } from 'lucide-react';

const AdminStaff: React.FC = () => {
  const [staff, setStaff] = useState<UserProfile[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingUid, setEditingUid] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'admin',
  });

  useEffect(() => {
    // Personelleri ve Rolleri çek
    const usersRef = ref(db, 'users');
    onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // isAdmin: true olan veya bir role sahip olanları getir
        const staffList = Object.values(data).filter((u: any) => u.isAdmin === true) as UserProfile[];
        setStaff(staffList);
      }
    });

    onValue(ref(db, 'roles'), (snapshot) => {
      const data = snapshot.val();
      if (data) setRoles(Object.values(data));
    });
  }, []);

  const resetForm = () => {
    setFormData({ email: '', password: '', displayName: '', role: 'admin' });
    setEditingUid(null);
    setIsModalOpen(false);
  };

  const handleEditInit = (u: UserProfile) => {
    setEditingUid(u.uid);
    setFormData({
      email: u.email,
      password: '', // Şifre düzenleme güvenlik gereği bu formda yok
      displayName: u.displayName || '',
      role: u.role || 'admin',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingUid) {
        // DÜZENLEME MODU
        await update(ref(db, `users/${editingUid}`), {
          displayName: formData.displayName,
          role: formData.role,
          // isAdmin her zaman true kalmalı personel için
          isAdmin: true
        });
        alert("Personel bilgileri güncellendi.");
      } else {
        // YENİ PERSONEL EKLEME MODU
        const firebaseConfig = (auth as any).app.options;
        const secondaryApp = initializeApp(firebaseConfig, "SecondaryApp_" + Date.now());
        const secondaryAuth = getAuth(secondaryApp);

        const userCredential = await createUserWithEmailAndPassword(secondaryAuth, formData.email, formData.password);
        const newUser = userCredential.user;

        await set(ref(db, `users/${newUser.uid}`), {
          uid: newUser.uid,
          email: formData.email,
          displayName: formData.displayName,
          isAdmin: true,
          role: formData.role,
          createdAt: Date.now()
        });

        await signOut(secondaryAuth);
        await deleteApp(secondaryApp);
        alert("Yeni personel hesabı oluşturuldu.");
      }
      resetForm();
    } catch (error: any) {
      console.error("Hata:", error);
      alert("İşlem sırasında hata oluştu: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-10 flex-shrink-0">
        <div>
          <h1 className="text-3xl font-black text-stone-900">Personel & Ekip</h1>
          <p className="text-stone-500 font-medium italic mt-1">Yönetim paneline erişimi olan çalışanları yönetin.</p>
        </div>
        <button 
          onClick={() => { setEditingUid(null); setIsModalOpen(true); }}
          className="bg-stone-900 text-white px-8 py-4 rounded-2xl font-bold flex items-center space-x-3 shadow-xl hover:bg-amber-800 transition-all active:scale-95"
        >
          <UserPlus size={20} />
          <span>Yeni Personel Ekle</span>
        </button>
      </div>

      <div className="flex-1 bg-white rounded-[3rem] border border-stone-100 shadow-sm overflow-hidden flex flex-col min-h-0">
        <div className="overflow-y-auto scrollbar-hide flex-1">
          <table className="w-full text-left">
            <thead className="bg-stone-50 sticky top-0 z-10 border-b border-stone-100 text-[10px] font-black text-stone-400 uppercase tracking-[0.2em]">
              <tr>
                <th className="px-8 py-6">Personel Bilgisi</th>
                <th className="px-8 py-6">E-posta</th>
                <th className="px-8 py-6">Yetki / Rol</th>
                <th className="px-8 py-6 text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {staff.map((u) => (
                <tr key={u.uid} className="hover:bg-stone-50/50 transition-colors group">
                  <td className="px-8 py-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center text-amber-800 font-black border border-stone-100">
                        {u.displayName?.charAt(0) || 'P'}
                      </div>
                      <span className="font-bold text-stone-900">{u.displayName}</span>
                    </div>
                  </td>
                  <td className="px-8 py-6 text-sm text-stone-500 font-medium">{u.email}</td>
                  <td className="px-8 py-6">
                    <div className="flex items-center space-x-2">
                      <ShieldCheck size={14} className="text-amber-600" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-stone-700">
                        {u.role === 'admin' ? 'Tam Yetkili Admin' : (roles.find(r => r.id === u.role)?.name || 'Tanımsız Rol')}
                      </span>
                    </div>
                  </td>
                  <td className="px-8 py-6 text-right">
                    <button 
                      onClick={() => handleEditInit(u)}
                      className="p-2 text-stone-300 hover:text-amber-800 hover:bg-amber-50 rounded-lg transition-all inline-flex items-center space-x-1"
                    >
                      <Edit2 size={18} />
                      <span className="text-[10px] font-black uppercase pr-1">Düzenle</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Personel Ekle/Düzenle Modalı */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[200] bg-stone-900/70 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
            <div className="p-8 border-b border-stone-50 flex items-center justify-between bg-stone-50/50">
              <h2 className="text-2xl font-black text-stone-900">{editingUid ? 'Personel Düzenle' : 'Yeni Personel Tanımla'}</h2>
              <button onClick={resetForm} className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-stone-400 hover:text-stone-900 shadow-sm transition-all">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-10 space-y-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Ad Soyad</label>
                  <div className="relative">
                    <User className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
                    <input 
                      required type="text"
                      value={formData.displayName}
                      onChange={e => setFormData({...formData, displayName: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 pl-14 pr-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold"
                      placeholder="Örn: Ahmet Yılmaz"
                    />
                  </div>
                </div>

                {!editingUid && (
                  <>
                    <div>
                      <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">E-posta Adresi</label>
                      <div className="relative">
                        <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
                        <input 
                          required type="email"
                          value={formData.email}
                          onChange={e => setFormData({...formData, email: e.target.value})}
                          className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 pl-14 pr-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold"
                          placeholder="ahmet@luminahome.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Şifre</label>
                      <input 
                        required type="password"
                        value={formData.password}
                        onChange={e => setFormData({...formData, password: e.target.value})}
                        className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold"
                        placeholder="••••••••"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Görev / Rol</label>
                  <select 
                    value={formData.role} 
                    onChange={e => setFormData({...formData, role: e.target.value})} 
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold text-stone-900"
                  >
                    <option value="admin">Sistem Yöneticisi (Tüm Yetkiler)</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="pt-6">
                <button 
                  disabled={loading}
                  type="submit"
                  className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold text-lg hover:bg-amber-800 transition-all flex items-center justify-center space-x-3 shadow-xl shadow-stone-900/10 active:scale-95 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="animate-spin" /> : <Check size={24} />}
                  <span>{editingUid ? 'Güncelle' : 'Personel Oluştur'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStaff;
